import { type RuleId, type Rule, RuleDetailsComponent, RuleHeadingComponent, Rules } from '@/rule';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  IpponButton,
  IpponButtonCard,
  IpponCard,
  IpponCheckbox,
  IpponContainer,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponIcon,
  IpponIconTile,
  IpponIon,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import { RulesSelection } from '@/diagnostic/domain/RulesSelection.ts';
import { type RefObject, useEffect, useState } from 'react';
import { SelectedRuleIds } from '@/diagnostic/domain/SelectedRuleIds.ts';
import { LoaderComponent, LoadingErrorComponent, useLoader } from '@/generic/loader';
import { useDialog } from '@/generic/react-tools';
import { type Page, Pagination, useInfiniteScroll } from '@/generic/pagination';
import { inject } from '@/diagnostic/injections.ts';
import { RULES_REPOSITORY } from '@/diagnostic/DiagnosticKeys.ts';
import { RulesSelectionLoadingComponent } from '@/diagnostic/primary/RulesSelectionLoadingComponent.tsx';

const RULES_PAGE_SIZE = 50;

const RuleSelectionComponent = ({
  rule,
  isSelected,
  onToggle,
}: {
  rule: Rule;
  isSelected: boolean;
  onToggle: (ruleId: RuleId) => void;
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const toggleCollapse = () => setCollapsed((collapsed) => !collapsed);
  const toggleSelection = () => onToggle(rule.id);
  const id = `rule-selection-${rule.id.value}`;

  return (
    <div
      className={clsx('ippon-toggle', { '-open': !collapsed })}
      data-selector="rules-selection.rule"
    >
      <div className="ippon-toggle--header">
        <button
          type="button"
          className="ippon-toggle--button"
          data-selector="rules-selection.rule.toggle"
          onClick={toggleCollapse}
          id={id}
          aria-expanded={!collapsed}
        >
          <IpponIon name="chevron-forward" className="ippon-toggle--icon" />
          <RuleHeadingComponent rule={rule} />
        </button>
        <div className="ippon-toggle--actions">
          <IpponCheckbox
            id={`${id}-checkbox`}
            checked={isSelected}
            onChange={toggleSelection}
            dataSelector="rules-selection.rule.checkbox"
          />
        </div>
      </div>
      {!collapsed && (
        <section aria-labelledby={id}>
          <div className="ippon-toggle--content">
            <IpponVSpace gap={8}>
              <RuleDetailsComponent rule={rule} />
            </IpponVSpace>
          </div>
        </section>
      )}
    </div>
  );
};

const RulesLoadMoreComponent = ({
  hasMore,
  failed,
  onRetry,
  sentinel,
}: {
  hasMore: boolean;
  failed: boolean;
  onRetry: () => void;
  sentinel: RefObject<HTMLDivElement | null>;
}) => {
  const { t } = useTranslation('diagnostic');

  if (!hasMore) {
    return null;
  }

  if (failed) {
    return (
      <IpponButton
        color="neutral"
        variant="text"
        size="small"
        onClick={onRetry}
        dataSelector="rules-selection.load-more.retry"
      >
        {t('rules-selection.load-more.retry')}
      </IpponButton>
    );
  }

  return (
    <div ref={sentinel} data-selector="rules-selection.load-more">
      <IpponText variant="body" size="small" color="neutral-tertiary-inversed">
        {t('rules-selection.load-more.loading')}
      </IpponText>
    </div>
  );
};

const RulesSelectionDialog = ({
  firstPage,
  selection,
  open = false,
  onClose,
  onSave,
}: {
  firstPage: Page<Rule>;
  selection: RulesSelection;
  open: boolean;
  onClose: () => void;
  onSave: (selection: RulesSelection) => void;
}) => {
  const { t } = useTranslation('diagnostic');
  const [rules, setRules] = useState(() => Rules.create().add(...firstPage.content));
  const [nextPagination, setNextPagination] = useState(() => firstPage.next);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [rulesSelection, setRulesSelection] = useState(selection);
  const selectedRuleIds = SelectedRuleIds.fromSelection(rulesSelection);

  const cancel = () => {
    setRulesSelection(selection);
    onClose();
  };

  const dialog = useDialog(open, cancel);

  const loadMore = () =>
    nextPagination.ifPresent(async (pagination) => {
      setLoadingMore(true);
      setLoadMoreFailed(false);
      try {
        const page = await inject(RULES_REPOSITORY).listActive(pagination);
        setRules((current) => current.add(...page.content));
        setNextPagination(page.next);
      } catch {
        setLoadMoreFailed(true);
      } finally {
        setLoadingMore(false);
      }
    });

  const sentinel = useInfiniteScroll<HTMLDivElement>({
    enabled: open && nextPagination.isPresent() && !loadingMore && !loadMoreFailed,
    onLoadMore: loadMore,
  });

  const onRuleToggle = (ruleId: RuleId) =>
    setRulesSelection((selection) => selection.toggle(ruleId));

  const onSelectAll = () => setRulesSelection((selection) => selection.selectAll());

  const onUnselectAll = () => setRulesSelection((selection) => selection.unselectAll());

  const save = () => {
    onSave(rulesSelection);
    onClose();
  };

  return (
    <dialog
      className="ippon-modal"
      id="rules-selection"
      data-selector="rules-selection"
      ref={dialog}
    >
      <div className="ippon-modal--header">
        <IpponHSpace align="middle" gap={16}>
          <IpponIconTile icon={{ name: 'list', variant: 'outline' }} size="small" />
          <IpponHSpaceSlot expand={true}>
            <IpponVSpace>
              <hgroup>
                <IpponTitle tag="h2" level={5}>
                  {t('rules-selection.title')}
                </IpponTitle>
                <IpponText
                  tag="p"
                  variant="body"
                  color="neutral-tertiary-inversed"
                  dataSelector="rules-selection.subtitle"
                >
                  {t('rules-selection.subtitle', {
                    count: rulesSelection.count,
                    total: rulesSelection.total,
                  })}
                </IpponText>
              </hgroup>
            </IpponVSpace>
          </IpponHSpaceSlot>
          <IpponButton
            color="neutral"
            variant="text"
            onClick={cancel}
            iconLeft={{ name: 'close' }}
            dataSelector="rules-selection.close"
          />
        </IpponHSpace>
      </div>
      <hr className="ippon-modal--separator" />
      <div className="ippon-modal--actions">
        <IpponHSpace gap={24} align="middle">
          <IpponButton
            variant="text"
            size="small"
            onClick={onSelectAll}
            disabled={rulesSelection.isAllSelected}
            dataSelector="rules-selection.select-all"
          >
            {t('rules-selection.select-all')}
          </IpponButton>
          <IpponButton
            color="neutral"
            variant="text"
            size="small"
            onClick={onUnselectAll}
            disabled={rulesSelection.isNoneSelected}
            dataSelector="rules-selection.unselect-all"
          >
            {t('rules-selection.unselect-all')}
          </IpponButton>
        </IpponHSpace>
      </div>
      <div className="ippon-modal--body">
        <IpponVSpace gap={16}>
          {rules.list.map((rule) => (
            <RuleSelectionComponent
              rule={rule}
              key={rule.id.value}
              onToggle={onRuleToggle}
              isSelected={rulesSelection.isRuleSelected(rule.id)}
            />
          ))}
          <RulesLoadMoreComponent
            hasMore={nextPagination.isPresent()}
            failed={loadMoreFailed}
            onRetry={loadMore}
            sentinel={sentinel}
          />
        </IpponVSpace>
      </div>
      <div className="ippon-modal--footer">
        {selectedRuleIds.isSuccess() ? (
          <IpponButton
            onClick={save}
            iconRight={{ name: 'save', variant: 'sharp' }}
            dataSelector="rules-selection.save"
          >
            {t('rules-selection.save')}
          </IpponButton>
        ) : null}
      </div>
    </dialog>
  );
};

const RulesSelectionResolvedComponent = ({
  firstPage,
  onSelected,
}: {
  firstPage: Page<Rule>;
  onSelected: (selected: SelectedRuleIds) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState(() => RulesSelection.of(firstPage.totalElementsCount));
  const { t } = useTranslation('diagnostic');

  const toggle = () => setOpen((open) => !open);

  const close = () => setOpen(false);

  useEffect(() => {
    SelectedRuleIds.fromSelection(selection).fold({
      error: () => undefined,
      success: onSelected,
    });
  }, [selection, onSelected]);

  const save = (saved: RulesSelection) => setSelection(saved);

  return (
    <>
      <IpponButtonCard shadow="l3" onClick={toggle} dataSelector="diagnostic.select-rules">
        <IpponHSpace align="middle" gap={16}>
          <IpponIconTile icon={{ name: 'hardware-chip', variant: 'sharp' }} size="large" />
          <IpponHSpaceSlot expand={true}>
            <IpponVSpace gap={4} align="left">
              <IpponText variant="body" color="neutral-tertiary-inversed">
                {t('diagnostic.select-rules.key')}
              </IpponText>
              <IpponText variant="label" size="large" dataSelector="diagnostic.select-rules.value">
                {t('diagnostic.select-rules.value', {
                  count: selection.count,
                  total: selection.total,
                })}
              </IpponText>
            </IpponVSpace>
          </IpponHSpaceSlot>
          <IpponIcon size={24} name="chevron-forward" />
        </IpponHSpace>
      </IpponButtonCard>
      <RulesSelectionDialog
        open={open}
        onClose={close}
        firstPage={firstPage}
        selection={selection}
        onSave={save}
      />
    </>
  );
};

export const RulesSelectionComponent = ({
  onSelected,
}: {
  onSelected: (selected: SelectedRuleIds) => void;
}) => {
  const rulesLoader = useLoader(
    () =>
      inject(RULES_REPOSITORY).listActive(Pagination.of({ page: 1, pageSize: RULES_PAGE_SIZE })),
    [],
  );

  const onLoading = () => <RulesSelectionLoadingComponent />;
  const onError = (error: Error) => (
    <IpponContainer>
      <IpponCard shadow="l3">
        <LoadingErrorComponent error={error} dataSelector="diagnostic.rules-selection.error" />
      </IpponCard>
    </IpponContainer>
  );

  const onSuccess = (firstPage: Page<Rule>) => (
    <RulesSelectionResolvedComponent firstPage={firstPage} onSelected={onSelected} />
  );

  return (
    <LoaderComponent
      loader={rulesLoader}
      onLoading={onLoading}
      onError={onError}
      onSuccess={onSuccess}
    />
  );
};
